/**
 * @fileoverview Leaderboard service for querying top players from Firebase
 *
 * Provides functions to fetch leaderboard data filtered by time control and region.
 */

import {
  collection,
  getDocs,
  getDoc,
  doc,
} from "firebase/firestore";
import { db } from "./firebaseConfig";
import { Region, TimeControlType } from "../MMR/types";

const USERS_COLLECTION = "users";
const LEADERBOARD_LIMIT = 20;

export interface LeaderboardEntry {
  rank: number;
  odiumI: string;
  username: string;
  rating: number;
  games: number;
  peak: number;
  region: Region;
  avatar?: string;
}

export interface LeaderboardResult {
  entries: LeaderboardEntry[];
  userRank: LeaderboardEntry | null;
}

/**
 * Get top players for a specific time control, optionally filtered by region
 * Uses client-side filtering to avoid complex Firestore index requirements
 */
export async function getLeaderboard(
  timeControl: TimeControlType,
  region?: Region | 'ALL'
): Promise<LeaderboardEntry[]> {
  const usersCollection = collection(db, USERS_COLLECTION);

  try {
    // Fetch all users - for small user bases this is simpler than complex indexes
    const querySnapshot = await getDocs(usersCollection);
    const entries: LeaderboardEntry[] = [];

    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      const profile = data.profile;

      // Skip if no profile data
      if (!profile?.username) return;

      // Skip bot accounts
      if (data.accountType === 'bot') return;

      const timeControlRating = profile?.mmr?.timeControlRatings?.[timeControl];

      // Skip if user hasn't completed placement for this time control
      if (!timeControlRating?.isPlaced) return;

      // Filter by region if specified
      if (region && region !== 'ALL' && profile.region !== region) return;

      entries.push({
        rank: 0, // Will be set after sorting
        odiumI: docSnapshot.id,
        username: profile.username,
        rating: timeControlRating.rating || 0,
        games: timeControlRating.games || 0,
        peak: timeControlRating.peak || timeControlRating.rating || 0,
        region: profile.region || 'UNKNOWN',
        avatar: profile.avatar,
      });
    });

    // Sort by rating descending
    entries.sort((a, b) => b.rating - a.rating);

    // Assign ranks and limit results
    const limitedEntries = entries.slice(0, LEADERBOARD_LIMIT);
    limitedEntries.forEach((entry, index) => {
      entry.rank = index + 1;
    });

    return limitedEntries;
  } catch (error) {
    throw error;
  }
}

/**
 * Get a specific user's rank for a time control
 * Uses client-side filtering to count users with higher ratings
 */
export async function getUserRank(
  userId: string,
  timeControl: TimeControlType,
  region?: Region | 'ALL'
): Promise<LeaderboardEntry | null> {
  try {
    // 1. Get user's current data
    const userDocRef = doc(db, USERS_COLLECTION, userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data();
    const profile = userData.profile;
    const timeControlRating = profile?.mmr?.timeControlRatings?.[timeControl];

    // User hasn't played this time control or isn't placed
    if (!timeControlRating || !timeControlRating.isPlaced) {
      return null;
    }

    // User doesn't match the region filter - don't show their rank
    if (region && region !== 'ALL' && profile.region !== region) {
      return null;
    }

    const userRating = timeControlRating.rating;

    // 2. Fetch all users and count those with higher ratings
    const usersCollection = collection(db, USERS_COLLECTION);
    const querySnapshot = await getDocs(usersCollection);

    let higherRatedCount = 0;

    querySnapshot.forEach((docSnapshot) => {
      // Skip self
      if (docSnapshot.id === userId) return;

      const data = docSnapshot.data();
      const otherProfile = data.profile;

      // Skip if no valid profile
      if (!otherProfile?.username) return;

      // Skip bot accounts
      if (data.accountType === 'bot') return;

      const otherRating = otherProfile?.mmr?.timeControlRatings?.[timeControl];

      // Skip if not placed in this time control
      if (!otherRating?.isPlaced) return;

      // Filter by region if specified
      if (region && region !== 'ALL' && otherProfile.region !== region) return;

      // Count if they have higher rating
      if (otherRating.rating > userRating) {
        higherRatedCount++;
      }
    });

    const rank = higherRatedCount + 1;

    return {
      rank,
      odiumI: userId,
      username: profile.username || 'Unknown',
      rating: userRating,
      games: timeControlRating.games || 0,
      peak: timeControlRating.peak || userRating,
      region: profile.region || 'UNKNOWN',
      avatar: profile.avatar,
    };
  } catch (error) {
    return null;
  }
}

/**
 * Combined function to get leaderboard with user's rank
 */
export async function getLeaderboardWithUserRank(
  timeControl: TimeControlType,
  region: Region | 'ALL',
  userId?: string
): Promise<LeaderboardResult> {
  // Fetch leaderboard (top 20)
  const entries = await getLeaderboard(timeControl, region);

  // Check if user is in the top 20
  let userRank: LeaderboardEntry | null = null;

  if (userId) {
    const userInTop = entries.find(entry => entry.odiumI === userId);

    if (!userInTop) {
      // User not in top 20, fetch their rank separately
      userRank = await getUserRank(userId, timeControl, region);
    }
  }

  return {
    entries,
    userRank,
  };
}

/**
 * Get display name for a region
 */
export function getRegionDisplayName(region: Region | 'ALL'): string {
  const regionNames: Record<Region | 'ALL', string> = {
    'ALL': 'All Regions',
    'NA': 'North America',
    'SA': 'South America',
    'EU': 'Europe',
    'AF': 'Africa',
    'AS': 'Asia',
    'OC': 'Oceania',
    'UNKNOWN': 'Unknown',
  };
  return regionNames[region] || region;
}

/**
 * Get display name for a time control
 */
export function getTimeControlDisplayName(timeControl: TimeControlType): string {
  const timeControlNames: Record<TimeControlType, string> = {
    'sprint': 'Sprint (1+2)',
    'standard': 'Standard (3+4)',
    'marathon': 'Marathon (10+10)',
  };
  return timeControlNames[timeControl] || timeControl;
}
